/**
 * Scope-key conventions — keep all message kinds in one IDB store keyed by a
 * string scope. Channels and DMs each get their own prefix so eviction, sync
 * metadata, and reads target them uniformly.
 *
 * Scope key formats (`<viewer>` = viewerCacheKeySegment of the signed-in person, CYBO-1846):
 *   - Channel: `<wsId>:<viewer>:ch:<channelId>`
 *   - DM:      `<wsId>:<viewer>:dm:<peerId>`     (the other party's user id)
 *
 * NOTE vs v1: v1 had a third `dma:<remoteAgentId>` scope for agent DMs. The
 * rewrite routes agent conversations through `agentStreamState` (live SDK
 * streaming, not selectDm), so there is no agent-DM scope here — channel + DM
 * only. If the rewrite ever persists agent-conversation history through the
 * same fetch path, add an `agentScope()` here keyed by `<wsId>:ag:<agentId>`.
 */

import { isViewerCacheKeySegment } from "./local-owner.js";

export type ScopeKind = "ch" | "dm";

// No viewer (signed out) builds no scope: "" never parses, so nothing is read or written.
export function channelScope(workspaceId: string, channelId: string, viewer: string): string {
  return viewer ? `${workspaceId}:${viewer}:ch:${channelId}` : "";
}

export function dmScope(workspaceId: string, peerId: string, viewer: string): string {
  return viewer ? `${workspaceId}:${viewer}:dm:${peerId}` : "";
}

// A key without the viewer segment (every pre-CYBO-1846 scope) parses to null, so
// evictLegacyScopes deletes it and getRecent never trusts it.
export function parseScope(
  scopeKey: string,
): { workspaceId: string; viewer: string; kind: ScopeKind; id: string } | null {
  const [workspaceId, viewer = "", kind] = scopeKey.split(":", 3);
  if (!workspaceId || !isViewerCacheKeySegment(viewer)) return null;
  if (kind !== "ch" && kind !== "dm") return null;
  const id = scopeKey.slice(workspaceId.length + viewer.length + kind.length + 3);
  return id ? { workspaceId, viewer, kind, id } : null; // null: the existing "not a scope" contract
}

/**
 * Belt-and-braces content guard for this on-device cache (Tauri iOS only —
 * this whole module is `cacheOn()`/`isTauriIOS()`-gated, see app.svelte.ts).
 * A scopeKey-indexed IndexedDB lookup is bounded to exact-scopeKey rows by
 * construction, so it should never surface another scope's content — but this
 * DB is long-lived, shared, browser-persisted storage (this cache layer itself
 * says it was "ported from v1"), so nothing here should blindly trust that
 * every row it can reach was written under the right key. This is the single
 * source of truth both `messages.ts` (the L2 read) and `state/app.svelte.ts`
 * (the render site) filter through: a row/live message is accepted for a scope
 * only if ITS OWN fields say it belongs there, never because it happened to
 * come back from that scope's query slot.
 *
 * NOTE: the actual reported prod incident this hardens against the same CLASS
 * of (the pane rendering a different channel's history while header/composer
 * stayed correct, no console error, reproduced in a plain Chrome tab) was
 * `selectWorkspace`'s default-channel bootstrap fetch racing a direct-URL
 * `selectChannel` with no channel-id guard — a plain network race, unrelated
 * to IndexedDB. That's fixed at the source (`selectWorkspace`, app.svelte.ts)
 * plus a matching belt-and-braces filter in `ChannelState.setMessages`/
 * `DmState.mergeServer` (core/state.svelte.ts), since `cacheOn()` gates this
 * whole cache module off outside the Tauri iOS shell.
 *
 * Minimal shape both `Message` (network, core/types.ts) and `CachedMessage`
 * (IDB, idb.ts) satisfy.
 */
export interface ScopableMessage {
  channelId: string | null;
  /** Optional on `Message` (not every wire payload carries it) — checked only
   *  when present so its absence can't fail an otherwise-correct match. */
  workspaceId?: string | null;
  fromId?: string;
  toId?: string | null;
}

/** True when `m` genuinely belongs to the channel scope `workspaceId:ch:channelId`. */
export function messageBelongsToChannel(
  m: ScopableMessage,
  workspaceId: string,
  channelId: string,
): boolean {
  return m.channelId === channelId && (m.workspaceId == null || m.workspaceId === workspaceId);
}

// The relay's mask for an erased person (server named-line.ts ERASED_ID): the rows of her pane carry it, not her id.
export const ERASED_PEER = "__erased__";
// The generic erased label (server named-line.ts / Logs names.ts); a drain-masked "Ca… (deleted)" name wins when sent.
export const ERASED_LABEL = "Name unavailable (deleted)";

/** A DM thread root whose other side is the erased mask: nobody can receive a reply (ThreadPanel, the Threads feed). */
export function isErasedDmRoot(
  m: { channelId?: string | null; fromId?: string | null; toId?: string | null } | null | undefined,
): boolean {
  return !!m && !m.channelId && (m.fromId === ERASED_PEER || m.toId === ERASED_PEER);
}

/** The label for an erased peer: the masked name the relay sent, else the generic one. */
export function erasedLabel(sent?: string | null): string {
  const s = sent?.trim();
  return s && s !== "Someone" && s !== "User" ? s : ERASED_LABEL;
}

/** True when `m` genuinely belongs to the DM scope `workspaceId:dm:peerId` —
 *  the peer must be the message's other party, or (with `isViewer`) a masked row
 *  whose other side is the viewer. channelId must be null: a DM scope never
 *  holds a channel message. */
export function messageBelongsToDm(
  m: ScopableMessage,
  workspaceId: string,
  peerId: string,
  isViewer?: (id: string | null | undefined) => boolean,
): boolean {
  if (m.channelId != null || (m.workspaceId != null && m.workspaceId !== workspaceId)) return false;
  if (m.fromId === peerId || m.toId === peerId) return true;
  // The erased mask names no peer: it counts only as the viewer's own side of the row.
  return (
    !!isViewer &&
    ((m.fromId === ERASED_PEER && isViewer(m.toId)) ||
      (m.toId === ERASED_PEER && isViewer(m.fromId)))
  );
}
